import { ImUser } from 'Src/ng2/shared/services/im-models/im-user';
import { IUser } from 'Src/ng2/shared/typings/interfaces/user.interface';
import { SpinnerModal } from 'Src/app/shared/modals/status-spinner/status-spinner.service';
import { ITileData } from 'Src/ng2/network/network-top-level/net-dash-tile-data/net-dash-tile-data.service';
import { ObjectCache } from 'Src/ng2/shared/services/object-cache/object-cache.service';
import { FormControl } from '@angular/forms';
import { pull, reduce, some } from 'lodash';
import { IStateService, IStateParamsService } from 'angular-ui-router';
import { switchMap, tap, shareReplay } from 'rxjs/operators';
import { NetworkMidLevelDataService } from './network-mid-level-data/network-mid-level-data.service';
import { Component, ViewChild, ElementRef, Inject, Input } from '@angular/core';
import { Observable, combineLatest, BehaviorSubject, Subject, of } from 'rxjs';
import {
  IRawNetworkMadlibData,
  INetworkMidLevelMadlibModel,
  IFormattedMadlibOptions,
  INetworkMidLevelGroupData,
  INetworkMidLevelFilterOption,
  INetworkMidLevelDropdownOption,
  INetworkMidLevelMadlibEvent,
  INetworkMidLevelFilterOptionHash,
} from './network-mid-level.models';

@Component({
  selector: 'network-mid-level',
  template: require('./network-mid-level.component.html'),
  styles: [require('./network-mid-level.component.scss')],
})
export class NetworkMidLevelComponent {
  @Input() initialState: { origin: string; defaults: any };
  @Input() topLevelFilters: string;
  @Input() currentUser: IUser;

  public setPanelShadowStyle: boolean = false;
  public rawMadlibData: IRawNetworkMadlibData;
  public madlibOptions: IFormattedMadlibOptions;
  public filterOptionHash: { [key: string]: any };
  public groupOptionHash: { [key: string]: any };
  public fociOptionHash: { [key: string]: any };

  public madlibModel: INetworkMidLevelMadlibModel = {
    focus: new FormControl(),
    filters: new FormControl(),
    group: new FormControl(),
  };

  /**
   * How is this different from the madlibModel?
   * MadlibModel is the current state of the filters whenever a user is interacting with them
   * MadlibModelForCurrentGroup is the madlib model that created the loaded UI.
   */
  public madlibModelForCurrentGroup: { focus: string; filters: Array<string>; group: string };
  public sort$ = new BehaviorSubject({ direction: null, key: null });
  public confirmationEvent$ = new Subject();
  public groupingData$: Observable<INetworkMidLevelGroupData>;

  public hoveredVizBar = { group: null, value: null };
  public hoveredTableRow = { group: null, value: null };

  @ViewChild('dashContainer', { static: false }) dashContainer: ElementRef;

  constructor(
    private dataService: NetworkMidLevelDataService,
    private objectCache: ObjectCache,
    private imUser: ImUser,
    @Inject('$state') public $state: IStateService,
    @Inject('$stateParams') public $stateParams: IStateParamsService,
    @Inject('SpinnerModal') public spinnerService: SpinnerModal,
  ) {}

  ngOnInit(): void {
    const madlibData$ = this.getMadlibData$().pipe(
      switchMap(rawMadlibData => {
        this.rawMadlibData = rawMadlibData;
        this.madlibOptions = this.dataService.getTableDataShapedToMadlib$(rawMadlibData);
        this.filterOptionHash = this.dataService.getOptionHash(rawMadlibData.filterOptions);
        this.groupOptionHash = this.dataService.getOptionHash(rawMadlibData.groupOptions);
        this.fociOptionHash = this.dataService.getOptionHash(rawMadlibData.fociOptions);

        const defaultMadlibState =
          this.initialState.origin === 'tileData'
            ? this.getTileMadlibState(this.initialState.defaults, this.filterOptionHash)
            : this.getUrlMadlibState(this.initialState.defaults);

        // Before setting the defaults we need to populate the filters with the proper options
        this.madlibModel.filters.setValue(this.dataService.getInitialFilters(rawMadlibData));
        this.setMadlibModelDefaults(this.madlibModel, defaultMadlibState);

        return of({
          madlibOptions: this.madlibOptions,
          filterOptionHash: this.filterOptionHash,
          groupOptionHash: this.groupOptionHash,
          madlibModel: this.madlibModel,
        });
      }),
      tap(() => this.confirmationEvent$.next()),
    );

    this.groupingData$ = combineLatest(
      madlibData$,
      this.madlibModel.focus.valueChanges,
      this.madlibModel.group.valueChanges,
      this.confirmationEvent$,
    ).pipe(
      tap(([madlibData]) => {
        this.spinnerService.activateSpinner('Loading...');
        // Keep tabs on this so we can roll back when user cancels superFilter updates
        this.madlibModelForCurrentGroup = {
          focus: madlibData.madlibModel.focus.value,
          filters: madlibData.madlibModel.filters.value,
          group: madlibData.madlibModel.group.value,
        };

        this.updateUrl(madlibData.madlibModel);

        this.sort$.next({
          key: 'FOCUS',
          // talk to product about this
          // direction: this.fociOptionHash[this.madlibModel.focus.value].sort.toLowerCase(),
          direction: 'desc',
        });
      }),
      switchMap(([madlibData, focus, group]) => {
        return this.dataService.getNetworkGroupData$(
          { focus, filters: madlibData.madlibModel.filters.value, group },
          madlibData.filterOptionHash,
          madlibData.groupOptionHash,
        );
      }),
      tap(() => this.spinnerService.deactivateSpinner()),
      shareReplay(1),
    );
  }

  public onVizBarHover($event: { group: string; value: string }): void {
    this.hoveredVizBar = $event ? $event : { group: null, value: null };
  }

  public onTableRowHover($event: { group: string; value: string }): void {
    this.hoveredTableRow = $event;
  }

  public updateSort($event: { key: string }, currentSort: { key: string; direction: 'asc' | 'desc' }): void {
    const keyIsCurrent = currentSort.key === $event.key;
    if (keyIsCurrent) this.sort$.next({ key: $event.key, direction: currentSort.direction === 'asc' ? 'desc' : 'asc' });
    else this.sort$.next({ key: $event.key, direction: 'asc' });
  }

  public goToDashboard(topLevelFiltersHash: string): void {
    this.$state.go('main.network.dashboard', { filters: topLevelFiltersHash });
  }

  public goToStudentLevel(
    rowEvent$: INetworkMidLevelGroupData['rowData'],
    madlibModelForCurrentGroup: { focus: string; filters: Array<string>; group: string },
    filterHash: INetworkMidLevelFilterOptionHash,
    fociOptions: Array<INetworkMidLevelDropdownOption>,
    currentUser: IUser,
  ): void {
    if (this.imUser.canSeeNetworkStuLvl(currentUser) && madlibModelForCurrentGroup.group === 'SCHOOL') {
      this.$state.go('main.network.studentLevel', {
        midlevelData: this.getQueryParams({ rowEvent$, fociOptions, filterHash, madlibModelForCurrentGroup }),
      });
    } else return;
  }

  public onMadlibModelChange(
    $event: INetworkMidLevelMadlibEvent,
    madlibModel: INetworkMidLevelMadlibModel,
    filterOptionHash: INetworkMidLevelFilterOptionHash,
  ): void {
    this.patchMadlib($event, madlibModel, filterOptionHash);
    this.updateUrl(madlibModel);
  }

  public onConfirm(): void {
    this.confirmationEvent$.next();
  }

  public onCancel(madlibModelForCurrentGroup): void {
    this.madlibModel.filters.patchValue(madlibModelForCurrentGroup.filters);
  }

  private getStudentFiltersModel(
    madlibModelForCurrentGroup,
    filterHash: INetworkMidLevelFilterOptionHash,
    schoolName: string,
  ): { [key: string]: Array<string> } {
    return reduce(
      madlibModelForCurrentGroup.filters,
      (acc, v, k) => {
        if (filterHash[k].category === 'Student Filter') acc[k] = v;
        return acc;
      },
      { schoolNameDbn: [schoolName] },
    );
  }

  private isStudentFilterModelModified(studentFilterModel, filterHash): boolean {
    return some(studentFilterModel, (v, k) => {
      const { filter_type, filter_values } = filterHash[k];
      if (k === 'schoolNameDbn') return false;
      if (filter_type === 'dropdown') return v;
      if (filter_type === 'multiselect') return studentFilterModel[k].length !== filter_values.length;
      else return false;
    });
  }

  private getQueryParams({ rowEvent$, fociOptions, filterHash, madlibModelForCurrentGroup }): string {
    const [stub] = rowEvent$;
    const schoolName = stub.meta;
    const { key: focusKey, label: focusLabel } = fociOptions.find(
      ({ key }) => key === madlibModelForCurrentGroup.focus,
    );
    const studentFilterModel = this.getStudentFiltersModel(madlibModelForCurrentGroup, filterHash, schoolName);
    const filterLabel = this.isStudentFilterModelModified(studentFilterModel, filterHash)
      ? 'some students'
      : 'all students';
    const filters = this.dataService.getFormattedFilters(studentFilterModel, filterHash);
    return this.objectCache.cacheObject({
      focusKey,
      focusLabel,
      filterLabel,
      schoolName,
      filters,
      madlibModel: this.objectCache.cacheObject({
        focus: madlibModelForCurrentGroup.focus,
        filters: madlibModelForCurrentGroup.filters,
        group: madlibModelForCurrentGroup.group,
      }),
    });
  }

  private patchMadlib(
    $event: { key: string; dimension: string },
    madlibModel: INetworkMidLevelMadlibModel,
    filterOptionHash: INetworkMidLevelFilterOptionHash,
  ): void {
    const filterPatch =
      $event.dimension !== 'filters'
        ? $event.key
        : {
            ...madlibModel.filters.value,
            ...{ [$event.key]: this.getFilterPatch({ $event, madlibModel, filterOptionHash }) },
          };
    madlibModel[$event.dimension].patchValue(filterPatch);
  }

  private getMadlibData$(): Observable<IRawNetworkMadlibData> {
    return this.dataService.getNetworkMadlibData$();
  }

  private updateUrl({ focus, filters, group }): void {
    const madlibModel = this.objectCache.cacheObject({
      focus: focus.value,
      filters: filters.value,
      group: group.value,
    });
    this.$state.go('.', { madlibModel }, { notify: false });
  }

  private setMadlibModelDefaults(
    madlibModel: INetworkMidLevelMadlibModel,
    { focus, filters, group },
  ): INetworkMidLevelMadlibModel {
    madlibModel.focus.patchValue(focus);
    madlibModel.group.patchValue(group);
    Object.entries(filters).forEach(([key, value]) =>
      madlibModel.filters.patchValue({ ...madlibModel.filters.value, [key]: value }),
    );
    return madlibModel;
  }

  // create type for this and function below
  private getTileMadlibState(originTile: ITileData, filterHash: INetworkMidLevelFilterOptionHash) {
    const { to_focus_key: focus, grouping_to_apply: group, filters_to_apply } = originTile;
    return { focus, filters: this.dataService.getDefaultFilters(filters_to_apply, filterHash), group };
  }

  private getUrlMadlibState({ focus, filters, group }) {
    return { focus, filters, group };
  }

  private getFilterPatch({ $event, madlibModel, filterOptionHash }): string | Array<string | number> {
    const { key, value } = $event;
    const rawFilterData = filterOptionHash[key];
    const currentFilterState = madlibModel.filters.value[key];
    const isMultiselect = rawFilterData.filter_type === 'multiselect';

    if (isMultiselect) {
      if (value === 'All') return this.getAllPatch(currentFilterState, rawFilterData);
      else if (currentFilterState.includes(value)) return this.getPatchWithValueRemoved(currentFilterState, value);
      else return [...currentFilterState, value];
    } else return this.getSingleSelectPatch(value);
  }

  private getSingleSelectPatch(value: string): string {
    return value === 'All' ? null : value;
  }

  private getPatchWithValueRemoved(currentFilterState: Array<string>, value: string): Array<string> {
    return [...pull([...currentFilterState], value)];
  }

  private getAllPatch(
    currentFilterState: Array<string>,
    rawFilterData: INetworkMidLevelFilterOption,
  ): Array<string | number> {
    const allFilterValues = rawFilterData.filter_values;
    const isEmptyOrPartial = !currentFilterState.length || currentFilterState.length !== allFilterValues.length;
    return isEmptyOrPartial ? allFilterValues : [];
  }
}
